import React, { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { getAPIBaseURL } from "../services/helpers"
import { getData, postData, updateData } from "../services/request"
import LatLngMapPicker from "../components/LatLngMapPicker"
import { appendReferralAttribution } from "../services/referralAttribution"
import { billingSummaryText } from "../utils/billingDisplay"
import { SALARY_CURRENCIES } from "../utils/salaryCurrencies"

const DOCUMENT_SUGGESTIONS = [
  "BSc Certificate",
  "NYSC Certificate",
  "MSc Certificate",
  "PhD Certificate",
  "CV",
  "Cover Letter",
]

const buildSteps = (requiresPayment) => [
  { key: "BASICS", label: "Job basics" },
  { key: "DETAILS", label: "Details & requirements" },
  { key: "SCREENING", label: "Screening & interview" },
  { key: "PREVIEW", label: requiresPayment ? "Preview & payment" : "Preview & submit" },
]

const EmployerNewJob = () => {
  const navigate = useNavigate()
  const { jobId: editJobId } = useParams()
  const isEditMode = Boolean(editJobId)
  const [loadingJob, setLoadingJob] = useState(isEditMode)
  const [currentStep, setCurrentStep] = useState("BASICS")
  const [submitting, setSubmitting] = useState(false)
  const [genError, setGenError] = useState("")
  const [genMsg, setGenMsg] = useState("")
  const [aiBrief, setAiBrief] = useState("")
  const [aiLoading, setAiLoading] = useState(false)
  const [paymentRequired, setPaymentRequired] = useState(true)
  const [billing, setBilling] = useState({
    currency: "NGN",
    amount: 10000,
    usedUsdFallback: false,
    countryCurrency: null,
  })

  useEffect(() => {
    const loadConfig = async () => {
      const url = `${getAPIBaseURL()}/employme/employer/job-posting/config`
      const res = await getData(url, {}, {})
      if (res.error === "" && res.result?.status === 200) {
        const data = res.result.data?.data || res.result.data
        setPaymentRequired(data?.paymentRequired !== false)
        if (data?.billingCurrency || data?.billingAmount != null) {
          setBilling({
            currency: data.billingCurrency || "NGN",
            amount: data.billingAmount != null ? data.billingAmount : 10000,
            usedUsdFallback: Boolean(data.usedUsdFallback),
            countryCurrency: data.countryCurrency || null,
          })
        }
      }
    }
    loadConfig()
  }, [])

  const formatDateInput = (iso) => {
    if (!iso) return ""
    try {
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return ""
      return d.toISOString().slice(0, 10)
    } catch {
      return ""
    }
  }

  const populateFormFromJob = (job) => {
    const locationStr =
      typeof job.location === "string"
        ? job.location
        : [job.location?.city, job.location?.state, job.location?.country]
            .filter(Boolean)
            .join(", ")

    const eligibilityQuestions = (job.screening?.eligibilityQuestions || [])
      .map((q) => q.question)
      .filter(Boolean)
      .join("\n")

    const requiredDocuments = (job.screening?.requiredDocuments || [])
      .map((d) => ({
        label: d.label || "",
        isMandatory: d.isMandatory !== false,
      }))
      .filter((d) => d.label)

    const interviewQuestions =
      Array.isArray(job.interviewQuestions) && job.interviewQuestions.length
        ? job.interviewQuestions
        : [""]

    setForm({
      title: job.title || "",
      location: locationStr,
      gender: job.gender || "Any",
      locationLat: job.location?.lat != null ? String(job.location.lat) : "",
      locationLng: job.location?.lng != null ? String(job.location.lng) : "",
      type: job.employmentType || job.type || "FULL_TIME",
      level: "MID",
      remoteType: job.locationType || job.remoteType || "ONSITE",
      candidatesNeeded: job.candidatesNeeded || 1,
      description: job.description || "",
      responsibilities: job.requirements || "",
      requirements: job.requirements || "",
      skills: Array.isArray(job.skills) ? job.skills.join(", ") : job.skills || "",
      salaryMin: job.salaryRange?.min != null ? String(job.salaryRange.min) : "",
      salaryMax: job.salaryRange?.max != null ? String(job.salaryRange.max) : "",
      currency: job.salaryRange?.currency || "NGN",
      jobStartDate: formatDateInput(job.jobStartDate),
      jobEndDate: formatDateInput(job.jobEndDate || job.applicationDeadline),
      interviewTimeLimitSeconds: job.interviewTimeLimitSeconds || 60,
      interviewMaxTakes: job.interviewMaxTakes || 1,
      interviewQuestions,
      eligibilityQuestions,
      requiredDocuments,
    })
  }

  useEffect(() => {
    if (!isEditMode || !editJobId) return

    const loadJob = async () => {
      setLoadingJob(true)
      setGenError("")
      const url = `${getAPIBaseURL()}/employme/employer/jobs/${editJobId}`
      const res = await getData(url, {}, {})
      setLoadingJob(false)
      if (res.error === "" && res.result?.status === 200) {
        const job = res.result.data?.data
        if (job) {
          populateFormFromJob(job)
        } else {
          setGenError("Job not found")
        }
      } else {
        setGenError(res.result?.data?.data || res.result?.data?.message || "Could not load job")
      }
    }
    loadJob()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, editJobId])

  const steps = buildSteps(paymentRequired)

  const [form, setForm] = useState({
    title: "",
    location: "",
    gender: "Any",
    locationLat: "",
    locationLng: "",
    type: "FULL_TIME",
    level: "MID",
    remoteType: "ONSITE",
    candidatesNeeded: 1,
    description: "",
    responsibilities: "",
    requirements: "",
    skills: "",
    salaryMin: "",
    salaryMax: "",
    currency: "NGN",
    jobStartDate: "",
    jobEndDate: "",
    interviewTimeLimitSeconds: 60,
    interviewMaxTakes: 1,
    interviewQuestions: [""],
    eligibilityQuestions: "",
    requiredDocuments: [],
  })

  const updateForm = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }))
  }

  const addDocumentRequirement = (label = "") => {
    const trimmed = label.trim()
    if (!trimmed) return
    setForm((prev) => {
      const exists = prev.requiredDocuments.some(
        (d) => d.label.toLowerCase() === trimmed.toLowerCase()
      )
      if (exists) return prev
      return {
        ...prev,
        requiredDocuments: [
          ...prev.requiredDocuments,
          { id: `doc-${Date.now()}-${prev.requiredDocuments.length}`, label: trimmed, isMandatory: true },
        ],
      }
    })
  }

  const updateDocumentRequirement = (index, patch) => {
    setForm((prev) => {
      const next = [...prev.requiredDocuments]
      next[index] = { ...next[index], ...patch }
      return { ...prev, requiredDocuments: next }
    })
  }

  const removeDocumentRequirement = (index) => {
    setForm((prev) => ({
      ...prev,
      requiredDocuments: prev.requiredDocuments.filter((_, i) => i !== index),
    }))
  }

  const needsWorksiteMap =
    form.remoteType === "ONSITE" || form.remoteType === "HYBRID"

  const validateBasics = () => {
    if (!form.title.trim()) return "Job title is required"
    if (!form.location.trim()) return "Location is required"
    if (needsWorksiteMap) {
      if (!form.locationLat || !form.locationLng) {
        return "Please set the job site on the map, search, or enter latitude and longitude"
      }
      const lat = Number(form.locationLat)
      const lng = Number(form.locationLng)
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return "Invalid map coordinates"
      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        return "Latitude must be between −90 and 90, longitude between −180 and 180"
      }
    }
    if (!form.jobStartDate) return "Job start date is required"
    if (!form.jobEndDate) return "Job end date is required"
    const start = new Date(form.jobStartDate)
    const end = new Date(form.jobEndDate)
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return "Invalid start/end date"
    }
    if (start > end) return "Job start date cannot be after end date"
    return ""
  }

  const validateDetails = () => {
    if (!form.description.trim()) return "Job description is required"
    return ""
  }

  const validateScreening = () => {
    const nonEmptyQuestions = form.interviewQuestions.filter((q) => q && q.trim().length > 0)
    if (nonEmptyQuestions.length === 0) return "Add at least one interview question"
    return ""
  }

  const applyJobDraftToForm = (draft) => {
    if (!draft || typeof draft !== "object") return

    const normalizeList = (value) => {
      if (!value) return ""
      if (Array.isArray(value)) {
        return value.map((v) => String(v).trim()).filter(Boolean).join("\n")
      }
      return String(value)
    }

    const normalizeSkills = (value) => {
      if (!value) return ""
      if (Array.isArray(value)) {
        return value.map((v) => String(v).trim()).filter(Boolean).join(", ")
      }
      return String(value)
    }

    setForm((prev) => ({
      ...prev,
      title: draft.title || prev.title,
      description: draft.description || prev.description,
      responsibilities: normalizeList(draft.responsibilities) || prev.responsibilities,
      requirements: normalizeList(draft.requirements) || prev.requirements,
      skills: normalizeSkills(draft.skills || draft.preferredSkills) || prev.skills,
    }))
    setGenMsg("AI draft applied. Review and adjust before publishing.")
  }

  const handleGenerateJobWithAI = async () => {
    setGenError("")
    setGenMsg("")
    setAiLoading(true)
    try {
      const url = `${getAPIBaseURL()}/employme/employer/ai/job-draft`
      const headers = { "Content-Type": "application/json" }
      const payload = {
        summary: aiBrief || form.description,
        roleTitle: form.title,
        seniorityLevel: form.level,
        location: form.location,
        remoteType: form.remoteType,
        employmentType: form.type,
        salaryMin: form.salaryMin,
        salaryMax: form.salaryMax,
        currency: form.currency,
        mustHaveSkills: form.skills,
        companyDescription: "",
      }
      const res = await postData(url, headers, JSON.stringify(payload))
      if (res.error === "") {
        if (res.result.status === 200) {
          const draft = res.result.data?.data
          if (draft) {
            applyJobDraftToForm(draft)
          } else {
            setGenError("AI did not return a usable draft")
          }
        } else {
          setGenError(res.result.data?.data || res.result.data?.message || "Could not generate job draft")
        }
      } else {
        setGenError(res.result?.data?.data || res.result?.data?.message || "Network error while generating draft")
      }
    } finally {
      setAiLoading(false)
    }
  }

  const handleImproveWithAI = async () => {
    setGenError("")
    setGenMsg("")
    setAiLoading(true)
    try {
      const url = `${getAPIBaseURL()}/employme/employer/ai/job-improve`
      const headers = { "Content-Type": "application/json" }
      const payload = {
        title: form.title,
        description: form.description,
        responsibilities: form.responsibilities,
        requirements: form.requirements,
        skills: form.skills,
        location: form.location,
        type: form.type,
        remoteType: form.remoteType,
        salaryMin: form.salaryMin,
        salaryMax: form.salaryMax,
        currency: form.currency,
      }
      const res = await postData(url, headers, JSON.stringify(payload))
      if (res.error === "") {
        if (res.result.status === 200) {
          const improved = res.result.data?.data
          if (improved && typeof improved === "object") {
            setForm((prev) => ({
              ...prev,
              title: improved.title || prev.title,
              description: improved.description || prev.description,
              responsibilities:
                (Array.isArray(improved.responsibilities)
                  ? improved.responsibilities.join("\n")
                  : improved.responsibilities || prev.responsibilities),
              requirements:
                (Array.isArray(improved.requirements)
                  ? improved.requirements.join("\n")
                  : improved.requirements || prev.requirements),
              skills:
                (Array.isArray(improved.skills)
                  ? improved.skills.join(", ")
                  : improved.skills || prev.skills),
            }))
            setGenMsg("AI improved your job description. Review before publishing.")
          } else {
            setGenError("AI did not return an improved draft")
          }
        } else {
          setGenError(res.result.data?.data || res.result.data?.message || "Could not improve job post")
        }
      } else {
        setGenError(res.result?.data?.data || res.result?.data?.message || "Network error while improving job post")
      }
    } finally {
      setAiLoading(false)
    }
  }

  const goNext = () => {
    setGenError("")
    let error = ""
    if (currentStep === "BASICS") error = validateBasics()
    if (currentStep === "DETAILS") error = validateDetails()
    if (currentStep === "SCREENING") error = validateScreening()
    if (error) {
      setGenError(error)
      return
    }
    const idx = steps.findIndex((s) => s.key === currentStep)
    if (idx < steps.length - 1) {
      setCurrentStep(steps[idx + 1].key)
    }
  }

  const goPrev = () => {
    setGenError("")
    const idx = steps.findIndex((s) => s.key === currentStep)
    if (idx > 0) {
      setCurrentStep(steps[idx - 1].key)
    }
  }

  const handleSubmit = async () => {
    setGenError("")
    setGenMsg("")
    const error = validateScreening() || validateDetails() || validateBasics()
    if (error) {
      setGenError(error)
      return
    }
    setSubmitting(true)
    const headers = { "Content-Type": "application/x-www-form-urlencoded" }
    const params = new URLSearchParams()
    params.append("title", form.title)
    params.append("location", form.location)
    params.append("gender", form.gender)
    params.append("type", form.type)
    params.append("level", form.level)
    params.append("remoteType", form.remoteType)
    params.append("candidatesNeeded", String(form.candidatesNeeded || 1))
    params.append("description", form.description)
    params.append("responsibilities", form.responsibilities)
    params.append("requirements", form.requirements)
    params.append("skills", form.skills)

    if (needsWorksiteMap && form.locationLat && form.locationLng) {
      params.append("locationLat", form.locationLat)
      params.append("locationLng", form.locationLng)
    }
    if (form.salaryMin) params.append("salaryMin", form.salaryMin)
    if (form.salaryMax) params.append("salaryMax", form.salaryMax)
    params.append("currency", form.currency)
    params.append("jobStartDate", form.jobStartDate)
    params.append("jobEndDate", form.jobEndDate)
    params.append("interviewTimeLimitSeconds", String(form.interviewTimeLimitSeconds || 60))
    params.append("interviewMaxTakes", String(form.interviewMaxTakes || 1))
    const questions = form.interviewQuestions.filter((q) => q && q.trim().length > 0)
    if (questions.length > 0) {
      params.append("interviewQuestions", JSON.stringify(questions))
    }
    if (form.eligibilityQuestions) {
      params.append("eligibilityQuestions", form.eligibilityQuestions)
    }
    if (form.requiredDocuments.length > 0) {
      params.append("requiredDocuments", JSON.stringify(form.requiredDocuments))
    }

    const url = isEditMode
      ? `${getAPIBaseURL()}/employme/employer/jobs/${editJobId}`
      : `${getAPIBaseURL()}/employme/employer/jobs`
    const res = isEditMode
      ? await updateData(url, headers, params.toString())
      : await postData(url, headers, appendReferralAttribution(params).toString())
    setSubmitting(false)
    if (res.error === "") {
      if (res.result.status === 201 || res.result.status === 200) {
        const savedJob = res.result.data?.data || {}
        const jobId = savedJob._id || savedJob.id || editJobId
        if (isEditMode) {
          setGenMsg("Job updated successfully.")
          navigate(`/employer/jobs/${jobId}`)
          return
        }
        setGenMsg(
          paymentRequired
            ? "Job created. Redirecting to payment..."
            : "Job created. You can submit it for review on the next screen."
        )
        if (jobId) {
          navigate(`/employer/jobs/${jobId}?from=new`)
        } else {
          navigate("/employer/jobs")
        }
      } else {
        setGenError(res.result.data?.message || res.result.data?.data || `Could not ${isEditMode ? "update" : "create"} job`)
      }
    } else {
      setGenError(res.result?.data?.data || res.result?.data?.message || "Network error")
    }
  }

  const renderStep = () => {
    if (currentStep === "BASICS") {
      return (
        <div className="space-y-5">
          <div>
            <label htmlFor="title" className="mb-1 block text-sm font-medium text-gray-900">
              Job title
            </label>
            <input
              id="title"
              value={form.title}
              onChange={(e) => updateForm({ title: e.target.value })}
              type="text"
              className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
              placeholder="e.g. Senior Frontend Engineer"
            />
          </div>
          <div>
            <label htmlFor="location" className="mb-1 block text-sm font-medium text-gray-900">
              Location
            </label>
            <input
              id="location"
              value={form.location}
              onChange={(e) => updateForm({ location: e.target.value })}
              type="text"
              className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
              placeholder="City, Country"
            />
          </div>

          <div>
            <label htmlFor="workMode" className="mb-1 block text-sm font-medium text-gray-900">
              Work mode
            </label>
            <select
              id="workMode"
              value={form.remoteType}
              onChange={(e) => {
                const v = e.target.value
                updateForm({
                  remoteType: v,
                  ...(v === "REMOTE" ? { locationLat: "", locationLng: "" } : {}),
                })
              }}
              className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
            >
              <option value="ONSITE">On-site</option>
              <option value="REMOTE">Remote</option>
              <option value="HYBRID">Hybrid</option>
            </select>
            {form.remoteType === "REMOTE" ? (
              <p className="mt-1 text-[11px] text-gray-500">
                Map pin is hidden for fully remote roles. Use the location field for region or time zone context if helpful.
              </p>
            ) : null}
          </div>

          {needsWorksiteMap ? (
            <div className="mt-1">
              <label className="mb-1 block text-sm font-medium text-gray-900">
                Precise job site (map & coordinates)
              </label>
              <LatLngMapPicker
                value={
                  form.locationLat && form.locationLng
                    ? { lat: Number(form.locationLat), lng: Number(form.locationLng) }
                    : null
                }
                disabled={submitting}
                onChange={({ lat, lng }) => {
                  updateForm({ locationLat: String(lat), locationLng: String(lng) })
                }}
              />
              {!(form.locationLat && form.locationLng) ? (
                <p className="mt-1 text-[11px] text-red-600">
                  Set a map pin, search for an address, use your location, or type latitude and longitude.
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-900">
                Job type
              </label>
              <select
                value={form.type}
                onChange={(e) => updateForm({ type: e.target.value })}
                className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
              >
                <option value="FULL_TIME">Full-time</option>
                <option value="PART_TIME">Part-time</option>
                <option value="CONTRACT">Contract</option>
                <option value="INTERNSHIP">Internship</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-900">
                Level
              </label>
              <select
                value={form.level}
                onChange={(e) => updateForm({ level: e.target.value })}
                className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
              >
                <option value="JUNIOR">Junior</option>
                <option value="MID">Mid</option>
                <option value="SENIOR">Senior</option>
                <option value="LEAD">Lead</option>
              </select>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-900">
              Candidates needed
            </label>
            <input
              value={form.candidatesNeeded}
              onChange={(e) => updateForm({ candidatesNeeded: Number(e.target.value) || 1 })}
              type="number"
              min="1"
              className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-900">
                Job start date
              </label>
              <input
                value={form.jobStartDate}
                onChange={(e) => updateForm({ jobStartDate: e.target.value })}
                type="date"
                className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-900">
                Job end date
              </label>
              <input
                value={form.jobEndDate}
                onChange={(e) => updateForm({ jobEndDate: e.target.value })}
                type="date"
                className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="mb-1 block text-sm font-medium text-gray-900">
              Gender preference
            </label>
            <select
              value={form.gender}
              onChange={(e) => updateForm({ gender: e.target.value })}
              className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
            >
              <option value="Any">Any</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
          </div>
        </div>
      )
    }

    if (currentStep === "DETAILS") {
      return (
        <div className="space-y-5">
          <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">
                  Let AI help you write this job post
                </p>
                <p className="mt-1 text-xs text-gray-600">
                  Describe the role and what you are looking for. AI will draft or improve the job
                  description and requirements. You can edit everything before publishing.
                </p>
                <textarea
                  value={aiBrief}
                  onChange={(e) => setAiBrief(e.target.value)}
                  rows={3}
                  className="mt-3 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
                  placeholder="e.g. We are hiring a senior frontend engineer to build our candidate dashboard using React and Tailwind..."
                />
                <p className="mt-2 text-[11px] text-gray-500">
                  AI suggestions may contain mistakes. Please review for accuracy and compliance before posting.
                </p>
              </div>
              <div className="mt-3 flex flex-col gap-2 sm:mt-0 sm:w-48">
                <button
                  type="button"
                  onClick={handleGenerateJobWithAI}
                  disabled={aiLoading}
                  className="w-full rounded-lg bg-black px-3 py-2 text-xs font-semibold text-white hover:bg-gray-900 disabled:cursor-not-allowed disabled:bg-gray-400"
                >
                  {aiLoading ? "Asking AI..." : "Generate full job post"}
                </button>
                <button
                  type="button"
                  onClick={handleImproveWithAI}
                  disabled={aiLoading}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-900 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {aiLoading ? "Asking AI..." : "Improve my text"}
                </button>
              </div>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-900">
              Job description
            </label>
            <textarea
              value={form.description}
              onChange={(e) => updateForm({ description: e.target.value })}
              rows={5}
              className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
              placeholder="Describe the role, mission, and impact..."
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-900">
              Key responsibilities
            </label>
            <textarea
              value={form.responsibilities}
              onChange={(e) => updateForm({ responsibilities: e.target.value })}
              rows={4}
              className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
              placeholder="List main responsibilities, one per line..."
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-900">
              Requirements
            </label>
            <textarea
              value={form.requirements}
              onChange={(e) => updateForm({ requirements: e.target.value })}
              rows={4}
              className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
              placeholder="Required experience, education, tools..."
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-900">
              Skills
            </label>
            <input
              value={form.skills}
              onChange={(e) => updateForm({ skills: e.target.value })}
              type="text"
              className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
              placeholder="Comma-separated skills (e.g. React, Node.js, SQL)"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-900">
                Salary min
              </label>
              <input
                value={form.salaryMin}
                onChange={(e) => updateForm({ salaryMin: e.target.value })}
                type="number"
                min="0"
                className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="0"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-900">
                Salary max
              </label>
              <input
                value={form.salaryMax}
                onChange={(e) => updateForm({ salaryMax: e.target.value })}
                type="number"
                min="0"
                className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="0"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-900">
                Currency
              </label>
              <select
                value={form.currency}
                onChange={(e) => updateForm({ currency: e.target.value })}
                className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
              >
                {(SALARY_CURRENCIES.some((item) => item.code === form.currency)
                  ? SALARY_CURRENCIES
                  : [{ code: form.currency, label: form.currency }, ...SALARY_CURRENCIES]
                ).map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )
    }

    if (currentStep === "SCREENING") {
      const questions = form.interviewQuestions
      const updateQuestion = (idx, value) => {
        const next = [...questions]
        next[idx] = value
        setForm((prev) => ({ ...prev, interviewQuestions: next }))
      }
      const addQuestion = () => {
        if (questions.length >= 10) return
        setForm((prev) => ({ ...prev, interviewQuestions: [...prev.interviewQuestions, ""] }))
      }
      const removeQuestion = (idx) => {
        if (questions.length === 1) return
        setForm((prev) => ({
          ...prev,
          interviewQuestions: prev.interviewQuestions.filter((_, i) => i !== idx),
        }))
      }

      return (
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-900">
                Time limit per question (seconds)
              </label>
              <input
                type="number"
                min="30"
                max="300"
                placeholder="60"
                value={form.interviewTimeLimitSeconds}
                onChange={(e) => {
                  const raw = e.target.value
                  if (raw === "") {
                    updateForm({ interviewTimeLimitSeconds: "" })
                    return
                  }
                  const n = Number(raw)
                  if (!Number.isFinite(n)) return
                  updateForm({ interviewTimeLimitSeconds: n })
                }}
                onBlur={() => {
                  const n = Number(form.interviewTimeLimitSeconds)
                  if (!Number.isFinite(n) || n <= 0) {
                    updateForm({ interviewTimeLimitSeconds: 60 })
                    return
                  }
                  const clamped = Math.min(300, Math.max(30, Math.round(n)))
                  if (clamped !== form.interviewTimeLimitSeconds) {
                    updateForm({ interviewTimeLimitSeconds: clamped })
                  }
                }}
                className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
              />
              <p className="mt-1 text-[11px] text-gray-500">Default is 60 seconds if left blank.</p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-900">
                Max takes per question
              </label>
              <input
                type="number"
                min="1"
                max="3"
                value={form.interviewMaxTakes}
                onChange={(e) =>
                  updateForm({
                    interviewMaxTakes: Number(e.target.value) || 1,
                  })
                }
                className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-900">
                Video interview questions
              </label>
              <span className="text-xs text-gray-500">
                {questions.length}/10
              </span>
            </div>
            <div className="space-y-3">
              {questions.map((q, idx) => (
                <div key={idx} className="flex gap-2">
                  <div className="mt-2 text-xs font-semibold text-gray-400">
                    {idx + 1}.
                  </div>
                  <div className="flex-1">
                    <input
                      value={q}
                      onChange={(e) => updateQuestion(idx, e.target.value)}
                      type="text"
                      className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
                      placeholder="Ask one clear, specific question..."
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeQuestion(idx)}
                    disabled={questions.length === 1}
                    className="mt-1 rounded-lg px-2 py-1 text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-40"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-3">
              <button
                type="button"
                onClick={addQuestion}
                disabled={questions.length >= 10}
                className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-900 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Add question
              </button>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-900">
              Eligibility questions (optional)
            </label>
            <textarea
              value={form.eligibilityQuestions}
              onChange={(e) => updateForm({ eligibilityQuestions: e.target.value })}
              rows={3}
              className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
              placeholder="List yes/no or multiple choice questions to ensure candidates meet minimum requirements."
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-900">
              Required documents (optional)
            </label>
            <p className="mb-2 text-xs text-gray-500">
              Define what candidates should upload. Mark each as mandatory or optional.
            </p>
            <div className="mb-3 flex flex-wrap gap-2">
              {DOCUMENT_SUGGESTIONS.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => addDocumentRequirement(suggestion)}
                  className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100"
                >
                  + {suggestion}
                </button>
              ))}
            </div>
            {form.requiredDocuments.length > 0 ? (
              <div className="space-y-2">
                {form.requiredDocuments.map((doc, index) => (
                  <div
                    key={doc.id || `doc-${index}`}
                    className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white p-3"
                  >
                    <input
                      type="text"
                      value={doc.label}
                      onChange={(e) => updateDocumentRequirement(index, { label: e.target.value })}
                      className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
                      placeholder="Document name"
                    />
                    <label className="flex items-center gap-2 text-xs text-gray-700">
                      <input
                        type="checkbox"
                        checked={doc.isMandatory !== false}
                        onChange={(e) =>
                          updateDocumentRequirement(index, { isMandatory: e.target.checked })
                        }
                        className="rounded border-gray-300"
                      />
                      Mandatory
                    </label>
                    <button
                      type="button"
                      onClick={() => removeDocumentRequirement(index)}
                      className="rounded-lg border border-red-200 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-500">No document requirements added yet.</p>
            )}
            <button
              type="button"
              onClick={() => addDocumentRequirement("Custom document")}
              className="mt-3 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-900 hover:bg-gray-50"
            >
              Add custom document
            </button>
          </div>
        </div>
      )
    }

    if (currentStep === "PREVIEW") {
      const nonEmpty = (t) => (t || "").split("\n").map((s) => s.trim()).filter(Boolean)
      const responsibilities = nonEmpty(form.responsibilities)
      const requirements = nonEmpty(form.requirements)
      const skills = (form.skills || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
      const eligibility = nonEmpty(form.eligibilityQuestions)
      const requiredDocs = form.requiredDocuments.filter((d) => d.label && d.label.trim())
      const interviewQs = form.interviewQuestions.filter((q) => q && q.trim().length > 0)

      return (
        <div className="space-y-5">
          <div className="rounded-lg border border-gray-200 bg-white p-4 sm:p-5">
            <h3 className="text-base font-semibold text-gray-900">
              {form.title || "Untitled role"}
            </h3>
            <p className="mt-1 text-xs text-gray-500">
              {form.location || "Location not set"} •{" "}
              {form.type === "FULL_TIME"
                ? "Full-time"
                : form.type === "PART_TIME"
                ? "Part-time"
                : form.type === "CONTRACT"
                ? "Contract"
                : "Internship"}{" "}
              •{" "}
              {form.remoteType === "REMOTE"
                ? "Remote"
                : form.remoteType === "HYBRID"
                ? "Hybrid"
                : "On-site"}
            </p>
            {needsWorksiteMap && form.locationLat && form.locationLng ? (
              <p className="mt-1 text-xs text-gray-500">
                Map pin:{" "}
                <span className="font-medium text-gray-800">
                  {Number(form.locationLat).toFixed(6)}, {Number(form.locationLng).toFixed(6)}
                </span>
              </p>
            ) : null}
            {(form.salaryMin || form.salaryMax) && (
              <p className="mt-1 text-xs text-gray-500">
                Salary:{" "}
                <span className="font-medium text-gray-900">
                  {form.salaryMin || "—"} - {form.salaryMax || "—"} {form.currency || "NGN"}
                </span>
              </p>
            )}
            {(form.jobStartDate || form.jobEndDate) && (
              <p className="mt-1 text-xs text-gray-500">
                Timeline: {form.jobStartDate || "—"} to {form.jobEndDate || "—"}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Candidates needed: {form.candidatesNeeded || 1}
            </p>
            {form.description ? (
              <p className="mt-3 whitespace-pre-line text-sm text-gray-700">
                {form.description}
              </p>
            ) : null}
          </div>

          {(responsibilities.length > 0 || requirements.length > 0 || skills.length > 0) && (
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-lg border border-gray-200 bg-white p-4 sm:p-5">
                <h4 className="text-sm font-semibold text-gray-900">Responsibilities</h4>
                {responsibilities.length ? (
                  <ul className="mt-2 list-disc pl-5 text-sm text-gray-700 space-y-1">
                    {responsibilities.map((r, idx) => (
                      <li key={`${idx}-${r.slice(0, 8)}`}>{r}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-2 text-sm text-gray-500">Not provided.</p>
                )}
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-4 sm:p-5">
                <h4 className="text-sm font-semibold text-gray-900">Requirements</h4>
                {requirements.length ? (
                  <ul className="mt-2 list-disc pl-5 text-sm text-gray-700 space-y-1">
                    {requirements.map((r, idx) => (
                      <li key={`${idx}-${r.slice(0, 8)}`}>{r}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-2 text-sm text-gray-500">Not provided.</p>
                )}
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 sm:p-5 lg:col-span-2">
                <h4 className="text-sm font-semibold text-gray-900">Skills</h4>
                {skills.length ? (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {skills.map((s) => (
                      <span
                        key={s}
                        className="inline-flex items-center rounded-full bg-white px-3 py-1 text-xs font-semibold text-gray-800 border border-gray-200"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-gray-500">No skills added.</p>
                )}
              </div>
            </div>
          )}

          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 sm:p-5">
            <h4 className="text-sm font-semibold text-gray-900">Interview configuration</h4>
            <p className="mt-1 text-xs text-gray-600">
              {form.interviewQuestions.filter((q) => q && q.trim().length > 0).length}{" "}
              question(s) • {form.interviewTimeLimitSeconds || 60}s per question •{" "}
              max {form.interviewMaxTakes || 1} take(s)
            </p>
            {interviewQs.length ? (
              <ul className="mt-3 list-disc pl-5 text-sm text-gray-700 space-y-1">
                {interviewQs.map((q, idx) => (
                  <li key={`${idx}-${q.slice(0, 10)}`}>{q}</li>
                ))}
              </ul>
            ) : null}
          </div>

          {(eligibility.length > 0 || requiredDocs.length > 0) && (
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-lg border border-gray-200 bg-white p-4 sm:p-5">
                <h4 className="text-sm font-semibold text-gray-900">Eligibility questions</h4>
                {eligibility.length ? (
                  <ul className="mt-2 list-disc pl-5 text-sm text-gray-700 space-y-1">
                    {eligibility.map((q, idx) => (
                      <li key={`${idx}-${q.slice(0, 10)}`}>{q}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-2 text-sm text-gray-500">None.</p>
                )}
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-4 sm:p-5">
                <h4 className="text-sm font-semibold text-gray-900">Required documents</h4>
                {requiredDocs.length ? (
                  <ul className="mt-2 list-disc pl-5 text-sm text-gray-700 space-y-1">
                    {requiredDocs.map((d, idx) => (
                      <li key={`${idx}-${d.label}`}>
                        {d.label}
                        {d.isMandatory !== false ? (
                          <span className="ml-2 text-xs font-medium text-amber-700">(required)</span>
                        ) : (
                          <span className="ml-2 text-xs font-medium text-gray-500">(optional)</span>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-2 text-sm text-gray-500">None.</p>
                )}
              </div>
            </div>
          )}

          <div className="rounded-lg border border-gray-200 bg-white p-4 sm:p-5">
            <h4 className="text-sm font-semibold text-gray-900">
              {paymentRequired ? "Payment summary" : "Publishing"}
            </h4>
            <p className="mt-1 text-xs text-gray-600">
              {paymentRequired
                ? `This job will be saved as a draft first. On the next screen you will pay based on your company country. ${billingSummaryText(
                    billing
                  )}`
                : "This job will be saved as a draft first. On the next screen, you can submit it for AI review at no cost."}
            </p>
          </div>
        </div>
      )
    }

    return null
  }

  const currentIndex = steps.findIndex((s) => s.key === currentStep)

  if (loadingJob) {
    return (
      <div className="mx-auto max-w-4xl">
        <div className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-500 shadow-sm">
          Loading job...
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-4xl">
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 bg-gray-50 px-4 py-4 sm:px-6 sm:py-5">
          <h2 className="text-lg font-semibold leading-snug text-gray-900 sm:text-xl">
            {isEditMode ? "Edit job post" : "New job post"}
          </h2>
          <p className="mt-1 text-sm leading-relaxed text-gray-600">
            {isEditMode
              ? "Update your job details, screening, and interview configuration."
              : `Create a job, configure screening, then ${paymentRequired ? "proceed to payment" : "submit for review"}.`}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2 sm:gap-3">
            {steps.map((step, idx) => {
              const active = step.key === currentStep
              const completed = idx < currentIndex
              return (
                <div key={step.key} className="flex items-center gap-1.5 text-xs sm:gap-2 sm:text-sm">
                  <div
                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-semibold ${
                      active
                        ? "border-black bg-black text-white"
                        : completed
                        ? "border-green-600 bg-green-600 text-white"
                        : "border-gray-300 bg-white text-gray-500"
                    }`}
                  >
                    {idx + 1}
                  </div>
                  <span
                    className={`whitespace-nowrap font-medium ${
                      active ? "text-gray-900" : "text-gray-500"
                    }`}
                  >
                    {step.label}
                  </span>
                  {idx < steps.length - 1 && (
                    <span className="mx-0.5 hidden h-px w-4 bg-gray-200 sm:mx-1 sm:block sm:w-8" />
                  )}
                </div>
              )
            })}
          </div>
        </div>
        <div className="p-4 sm:p-6">
          {genError ? (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">
              {genError}
            </div>
          ) : null}
          {genMsg ? (
            <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 text-sm font-medium text-green-700">
              {genMsg}
            </div>
          ) : null}
          {renderStep()}
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              onClick={goPrev}
              disabled={currentIndex === 0 || submitting}
              className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Previous
            </button>
            {currentStep === "PREVIEW" ? (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="rounded-lg bg-black px-5 py-2.5 text-sm font-semibold text-white hover:bg-gray-900 disabled:cursor-not-allowed disabled:bg-gray-400"
              >
                {submitting
                  ? isEditMode
                    ? "Saving changes..."
                    : "Creating job..."
                  : isEditMode
                  ? "Save changes"
                  : paymentRequired
                  ? "Save & continue to payment"
                  : "Save & continue"}
              </button>
            ) : (
              <button
                type="button"
                onClick={goNext}
                disabled={submitting}
                className="rounded-lg bg-black px-5 py-2.5 text-sm font-semibold text-white hover:bg-gray-900 disabled:cursor-not-allowed disabled:bg-gray-400"
              >
                Continue
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default EmployerNewJob

